import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Wrench, Wallet, Clock, BookOpen, CheckCircle2, Upload, Shield, ArrowRight, Phone,
} from "lucide-react";
import { toast } from "sonner";
import { useCatalog } from "../contexts/CatalogContext";
import { API } from "../lib/api";

const benefits = [
  { icon: Wallet, title: "Weekly Payouts", desc: "Direct bank/UPI transfer every Friday." },
  { icon: Clock, title: "Flexible Hours", desc: "Pick the slots that fit your day." },
  { icon: BookOpen, title: "Free Training", desc: "Brand-specific upskilling sessions." },
  { icon: Shield, title: "App-Managed Jobs", desc: "Schedule, navigation, payments — all in one place." },
];

const experienceLevels = ["Fresher", "1-2 yrs", "3-5 yrs", "5+ yrs"];

export default function JoinTechnician() {
  const { categories: CATEGORIES, CITIES } = useCatalog();
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    city: "lucknow",
    services: [],
    brands: [],
    experience: "Fresher",
    aadhar_number: "",
    aadhar_front_name: "",
    aadhar_back_name: "",
    profile_photo_name: "",
    certification_name: "",
    bank_account: "",
    ifsc: "",
    upi_id: "",
    agreed: false,
  });

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleService = (id) => {
    setForm((f) => {
      const has = f.services.includes(id);
      return { ...f, services: has ? f.services.filter((s) => s !== id) : [...f.services, id] };
    });
  };

  const toggleBrand = (b) => {
    setForm((f) => {
      const has = f.brands.includes(b);
      return { ...f, brands: has ? f.brands.filter((x) => x !== b) : [...f.brands, b] };
    });
  };

  const eligibleBrands = useMemo(() => {
    const set = new Set();
    form.services.forEach((sid) => {
      const c = CATEGORIES.find((c) => c.id === sid);
      if (c) c.brands.forEach((b) => set.add(b));
    });
    return Array.from(set);
  }, [form.services]);

  const handleFile = (key) => (e) => {
    const f = e.target.files?.[0];
    if (f) update({ [key]: f.name });
  };

  const sendOtp = async () => {
    if (!/^\d{10}$/.test(form.phone)) {
      toast.error("Enter a valid 10-digit phone");
      return;
    }
    // MOCKED: Bypass actual OTP API for development
    console.log("📱 OTP (MOCKED) for:", form.phone);
    setOtpSent(true);
    toast.success("OTP sent! Use 123456 (development - mocked).");
  };

  const verifyOtp = async () => {
    // MOCKED: Accept any 6-digit code for development
    if (!/^\d{6}$/.test(otp)) {
      toast.error("Enter 6-digit OTP (use 123456)");
      return;
    }
    console.log("🔐 OTP verified (MOCKED):", otp);
    setOtpVerified(true);
    toast.success("Phone verified ✓");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!otpVerified) return toast.error("Please verify your phone with OTP.");
    if (form.services.length === 0) return toast.error("Pick at least one service category.");
    if (!form.full_name || !form.aadhar_number) return toast.error("Please fill all required fields.");
    if (!form.agreed) return toast.error("Please accept the agreement.");
    setSubmitting(true);
    try {
      console.log("🔧 Submitting technician application to:", `${API}/technicians/apply`);
      console.log("📦 Form data:", form);
      const res = await axios.post(`${API}/technicians/apply`, form);
      console.log("✅ Application submitted successfully:", res.data);
      setSubmitted(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Application submitted! Check your phone for WhatsApp confirmation.");
    } catch (err) {
      console.error("❌ Submission error:", {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
        url: err.config?.url,
      });
      const errorMsg = err.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-testid="technician-success">
        <div className="bg-white rounded-2xl border border-slate-200 card-shadow p-8 sm:p-10 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-[#22C55E]/10 flex items-center justify-center">
            <CheckCircle2 className="w-12 h-12 text-[#22C55E]" />
          </div>
          <h1 className="font-display font-extrabold text-3xl text-slate-900 mt-5">Application received!</h1>
          <p className="text-slate-600 mt-2">Our team will call you within 24-48 hours to schedule a quick onboarding.</p>
          <div className="mt-6 inline-block bg-[#1A3C6E] text-white rounded-xl px-6 py-3">
            <span className="text-xs font-semibold opacity-80 block">Reference number</span>
            <span className="font-display font-extrabold text-xl tracking-wider" data-testid="technician-ref">#{submitted.ref_id}</span>
          </div>
          <p className="text-xs text-slate-500 mt-6">You'll also get a WhatsApp confirmation on {submitted.phone}.</p>
          <Link to="/" className="inline-block mt-8 text-sm font-semibold text-[#FF6B2B]">← Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="bg-[#1A3C6E] text-white" data-testid="join-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-white/10 text-xs font-semibold border border-white/20">For Technicians</span>
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl mt-4 leading-tight">
                Earn With <span className="text-[#0EA5A4]">Repairro</span> — Work on Your Terms
              </h1>
              <p className="mt-4 text-slate-200 text-lg">
                Join 30+ verified technicians earning ₹25,000+ a month through Repairro's app-managed jobs in Lucknow.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3 max-w-md">
                {benefits.map((b) => (
                  <div key={b.title} className="flex items-start gap-2 bg-white/5 rounded-xl p-3 border border-white/10">
                    <b.icon className="w-5 h-5 text-[#FF6B2B] flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs font-semibold">{b.title}</div>
                      <div className="text-[10px] text-slate-300">{b.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/5 rounded-2xl border border-white/10 p-6 backdrop-blur">
              <h3 className="font-display font-bold text-lg">Eligibility</h3>
              <ul className="mt-4 space-y-2 text-sm text-slate-200">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#22C55E]" /> Technical knowledge in any appliance category</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#22C55E]" /> Own a smartphone with internet</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#22C55E]" /> Age 18-50 years</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#22C55E]" /> Resident of service city (Lucknow)</li>
              </ul>
              <p className="text-xs text-slate-400 mt-4">No upfront fees. No commitments. Cancel anytime.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FORM */}
      <form onSubmit={submit} className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="technician-form">
        <h2 className="font-display font-bold text-2xl text-slate-900">Register as a Technician</h2>
        <p className="text-sm text-slate-500 mt-1">Fields marked * are required.</p>

        {/* Personal */}
        <Section title="Personal Details">
          <Field label="Full Name *">
            <input
              data-testid="tech-name"
              required
              value={form.full_name}
              onChange={(e) => update({ full_name: e.target.value })}
              className={inputCls}
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Phone Number * (10 digit)">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    data-testid="tech-phone"
                    required
                    inputMode="numeric"
                    disabled={otpVerified}
                    value={form.phone}
                    onChange={(e) => update({ phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                    className={`${inputCls} pl-10 disabled:bg-slate-50 disabled:text-slate-500`}
                  />
                </div>
                {!otpVerified && (
                  <button
                    type="button"
                    data-testid="tech-send-otp"
                    onClick={sendOtp}
                    className="px-4 rounded-xl bg-[#1A3C6E] hover:bg-[#132D53] text-white font-semibold text-sm whitespace-nowrap"
                  >
                    {otpSent ? "Resend OTP" : "Send OTP"}
                  </button>
                )}
                {otpVerified && (
                  <span className="px-3 py-2 rounded-xl bg-[#22C55E]/10 text-[#22C55E] font-semibold text-sm flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" /> Verified
                  </span>
                )}
              </div>
              {otpSent && !otpVerified && (
                <div className="mt-2 flex gap-2 items-center">
                  <input
                    data-testid="tech-otp-input"
                    placeholder="Enter 6-digit OTP (use 123456)"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className={inputCls}
                  />
                  <button
                    type="button"
                    data-testid="tech-verify-otp"
                    onClick={verifyOtp}
                    className="px-4 py-2 rounded-xl bg-[#FF6B2B] hover:bg-[#E65A20] text-white font-semibold text-sm whitespace-nowrap"
                  >
                    Verify
                  </button>
                </div>
              )}
            </Field>
            <Field label="Email">
              <input
                data-testid="tech-email"
                type="email"
                value={form.email}
                onChange={(e) => update({ email: e.target.value })}
                className={inputCls}
              />
            </Field>
          </div>
          <Field label="City *">
            <select data-testid="tech-city" value={form.city} onChange={(e) => update({ city: e.target.value })} className={inputCls + " bg-white"}>
              {CITIES.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
              <option value="other">Other</option>
            </select>
          </Field>
        </Section>

        {/* Services */}
        <Section title="Services You Can Provide *">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {CATEGORIES.map((c) => {
              const active = form.services.includes(c.id);
              const Icon = c.icon;
              return (
                <button
                  type="button"
                  key={c.id}
                  data-testid={`tech-service-${c.id}`}
                  onClick={() => toggleService(c.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    active ? "border-[#1A3C6E] bg-[#1A3C6E]/5" : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: c.bg, color: c.color }}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="font-semibold text-slate-900 mt-2 text-sm">{c.short}</div>
                  {active && <CheckCircle2 className="w-4 h-4 text-[#22C55E] mt-2" />}
                </button>
              );
            })}
          </div>
        </Section>

        {/* Brands */}
        {eligibleBrands.length > 0 && (
          <Section title="Brands You're Expert In">
            <div className="flex flex-wrap gap-2" data-testid="tech-brands-list">
              {eligibleBrands.map((b) => {
                const active = form.brands.includes(b);
                return (
                  <button
                    type="button"
                    key={b}
                    data-testid={`tech-brand-${b.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '')}`}
                    onClick={() => toggleBrand(b)}
                    className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-all ${
                      active ? "bg-[#FF6B2B] text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-[#FF6B2B]"
                    }`}
                  >
                    {b}
                  </button>
                );
              })}
            </div>
          </Section>
        )}

        {/* Experience */}
        <Section title="Years of Experience *">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="tech-experience-group">
            {experienceLevels.map((lvl) => {
              const active = form.experience === lvl;
              return (
                <button
                  type="button"
                  key={lvl}
                  data-testid={`tech-experience-${lvl.replace(/\s+/g, '-').replace(/\+/g, 'plus')}`}
                  onClick={() => update({ experience: lvl })}
                  className={`py-3 rounded-xl border-2 font-semibold text-sm transition-all ${
                    active ? "border-[#1A3C6E] bg-[#1A3C6E] text-white" : "border-slate-200 text-slate-700 hover:border-slate-300"
                  }`}
                >
                  {lvl}
                </button>
              );
            })}
          </div>
        </Section>

        {/* KYC */}
        <Section title="KYC & Identity">
          <Field label="Aadhar Card Number *">
            <input
              data-testid="tech-aadhar"
              required
              inputMode="numeric"
              value={form.aadhar_number}
              onChange={(e) => update({ aadhar_number: e.target.value.replace(/\D/g, "").slice(0, 12) })}
              className={inputCls}
              placeholder="12 digits"
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FileField label="Aadhar Front *" testid="tech-aadhar-front" file={form.aadhar_front_name} onChange={handleFile("aadhar_front_name")} />
            <FileField label="Aadhar Back *" testid="tech-aadhar-back" file={form.aadhar_back_name} onChange={handleFile("aadhar_back_name")} />
            <FileField label="Profile Photo *" testid="tech-photo" file={form.profile_photo_name} onChange={handleFile("profile_photo_name")} />
            <FileField label="Certification (optional)" testid="tech-cert" file={form.certification_name} onChange={handleFile("certification_name")} />
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Note: File upload is in MVP mode — filenames are recorded for now. Full upload via secure storage will be enabled in the next phase.
          </p>
        </Section>

        {/* Payment */}
        <Section title="Payment Details (for weekly payouts)">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Bank Account Number">
              <input data-testid="tech-bank" value={form.bank_account} onChange={(e) => update({ bank_account: e.target.value })} className={inputCls} />
            </Field>
            <Field label="IFSC Code">
              <input data-testid="tech-ifsc" value={form.ifsc} onChange={(e) => update({ ifsc: e.target.value.toUpperCase() })} className={inputCls} />
            </Field>
          </div>
          <Field label="UPI ID (optional)">
            <input data-testid="tech-upi" value={form.upi_id} onChange={(e) => update({ upi_id: e.target.value })} className={inputCls} placeholder="yourname@upi" />
          </Field>
        </Section>

        {/* Agreement */}
        <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-5 flex items-start gap-3" data-testid="tech-agreement-row">
          <input
            id="agree"
            type="checkbox"
            data-testid="tech-agree-checkbox"
            checked={form.agreed}
            onChange={(e) => update({ agreed: e.target.checked })}
            className="mt-1 w-5 h-5 accent-[#FF6B2B]"
          />
          <label htmlFor="agree" className="text-sm text-slate-700">
            I confirm the information above is accurate. I agree to Repairro's <Link to="/privacy" className="text-[#0EA5A4] font-semibold">Privacy Policy</Link> and Technician Code of Conduct.
          </label>
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid="tech-submit"
          className="mt-6 w-full bg-[#FF6B2B] hover:bg-[#E65A20] text-white font-semibold py-4 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {submitting ? "Submitting..." : "Submit Application"} <ArrowRight className="w-5 h-5" />
        </button>
      </form>
    </div>
  );
}

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/10 outline-none";

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h3 className="font-display font-bold text-lg text-slate-900 mb-4">{title}</h3>
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 card-shadow">{children}</div>
  </section>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-sm font-medium text-slate-700">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const FileField = ({ label, file, onChange, testid }) => (
  <Field label={label}>
    <label className="flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-slate-200 hover:border-[#FF6B2B] cursor-pointer transition-all">
      <Upload className="w-4 h-4 text-slate-500" />
      <span className="text-sm text-slate-600 truncate">{file || "Choose file"}</span>
      <input type="file" className="hidden" data-testid={testid} onChange={onChange} accept="image/*,application/pdf" />
    </label>
  </Field>
);
